import type { CSSProperties } from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { FALLBACK_LEGEND } from "./legendFallback";
import type {
  BoardLegendItem,
  BoardLegendLink,
  BoardLegendPatch,
  LegendLayer,
  LegendVisualStyle,
} from "./legendTypes";

export const LEGEND_CHIP_SHAPE =
  "inline-flex h-[18px] items-center rounded-[3px] px-1.5 text-[12px] font-medium leading-none";

export const LEGEND_SOLID_FILL: Record<string, string> = {
  gx: "bg-[#db7a00]",
  tw: "bg-[#5856d6]",
  cg: "bg-[#8e8e93]",
  xyz: "bg-[#7b3fa8]",
  cosy: "bg-[#0e7c78]",
  geo: "bg-[#d17a1a]",
  nm: "bg-[#007aff]",
  you: "bg-[#ff3b30]",
  co: "bg-[#2aa84a]",
};

export const IDEA_OUTLINE_CLASS =
  "border border-dashed border-[#C4A574] bg-transparent text-[#5B4A2A] dark:bg-white dark:text-[#5B4A2A]";

type LegendRow = {
  id?: number;
  token?: string;
  layer?: string;
  line_key?: string;
  label?: string;
  description?: string | null;
  sort_order?: number;
  show_in_legend?: boolean;
  visual_style?: string;
  bar_hex?: string | null;
  bar_text_hex?: string | null;
  outline_hex?: string | null;
  outline_text_hex?: string | null;
  soft_dot_hex?: string | null;
  soft_chip_bg?: string | null;
  soft_chip_text?: string | null;
  links?: unknown;
  mac_account_path?: string | null;
  active?: boolean;
};

function asLayer(value: string | undefined): LegendLayer {
  if (value === "idea" || value === "matter") return value;
  return "biz";
}

function asStyle(value: string | undefined): LegendVisualStyle {
  return value === "dashed" ? "dashed" : "solid";
}

function blankToNull(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function parseLegendLinks(raw: unknown): BoardLegendLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const row = entry as Record<string, unknown>;
    const url = typeof row.url === "string" ? row.url.trim() : "";
    if (!url) return [];
    const link: BoardLegendLink = {
      kind: typeof row.kind === "string" && row.kind ? row.kind : "public_url",
      label: typeof row.label === "string" && row.label ? row.label : url,
      url,
    };
    if (typeof row.token === "string" && row.token) link.token = row.token;
    return [link];
  });
}

export function rowToLegendItem(
  row: LegendRow,
  source: BoardLegendItem["source"] = "db",
): BoardLegendItem {
  return {
    id: typeof row.id === "number" ? row.id : undefined,
    token: row.token ?? "",
    layer: asLayer(row.layer),
    line_key: row.line_key ?? "",
    label: row.label ?? "",
    description: row.description ?? null,
    sort_order: Number(row.sort_order ?? 0),
    show_in_legend: row.show_in_legend !== false,
    visual_style: asStyle(row.visual_style),
    bar_hex: row.bar_hex ?? null,
    bar_text_hex: row.bar_text_hex ?? null,
    outline_hex: row.outline_hex ?? null,
    outline_text_hex: row.outline_text_hex ?? null,
    soft_dot_hex: row.soft_dot_hex ?? null,
    soft_chip_bg: row.soft_chip_bg ?? null,
    soft_chip_text: row.soft_chip_text ?? null,
    links: parseLegendLinks(row.links),
    mac_account_path: row.mac_account_path ?? null,
    active: row.active !== false,
    source,
  };
}

const RETIRED_LIANLIANKAN = "idea.lianliankan";

/**
 * DB was rolled back to idea.lianliankan. Display the opened line anyway.
 * A real `biz.niuma` row wins; otherwise the fallback chip fills the gap.
 */
export function alignNiumaLegend(items: BoardLegendItem[]): BoardLegendItem[] {
  const rest = items.filter((item) => item.token !== RETIRED_LIANLIANKAN);
  if (rest.some((item) => item.token === "biz.niuma")) return rest;
  const niuma = FALLBACK_LEGEND.find((item) => item.token === "biz.niuma");
  return niuma ? [...rest, niuma] : rest;
}

export function visibleLegendItems(items: BoardLegendItem[]) {
  return items
    .filter((item) => item.active && item.show_in_legend)
    .slice()
    .sort(
      (a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label),
    );
}

export function groupLegend(items: BoardLegendItem[]) {
  const visible = visibleLegendItems(items);
  return {
    biz: visible.filter((item) => item.layer === "biz"),
    idea: visible.filter((item) => item.layer === "idea"),
    matter: visible.filter((item) => item.layer === "matter"),
  };
}

export function allowedLegendPatch(
  input: Partial<BoardLegendPatch> & Record<string, unknown>,
): BoardLegendPatch {
  const links = parseLegendLinks(input.links);
  return {
    label: String(input.label ?? "").trim(),
    description: blankToNull(
      typeof input.description === "string" ? input.description : null,
    ),
    visual_style: asStyle(
      typeof input.visual_style === "string" ? input.visual_style : undefined,
    ),
    sort_order: Number(input.sort_order ?? 0),
    show_in_legend: input.show_in_legend !== false,
    bar_hex: blankToNull(
      typeof input.bar_hex === "string" ? input.bar_hex : null,
    ),
    bar_text_hex: blankToNull(
      typeof input.bar_text_hex === "string" ? input.bar_text_hex : null,
    ),
    outline_hex: blankToNull(
      typeof input.outline_hex === "string" ? input.outline_hex : null,
    ),
    outline_text_hex: blankToNull(
      typeof input.outline_text_hex === "string"
        ? input.outline_text_hex
        : null,
    ),
    soft_dot_hex: blankToNull(
      typeof input.soft_dot_hex === "string" ? input.soft_dot_hex : null,
    ),
    soft_chip_bg: blankToNull(
      typeof input.soft_chip_bg === "string" ? input.soft_chip_bg : null,
    ),
    soft_chip_text: blankToNull(
      typeof input.soft_chip_text === "string" ? input.soft_chip_text : null,
    ),
    links,
    mac_account_path: blankToNull(
      typeof input.mac_account_path === "string"
        ? input.mac_account_path
        : null,
    ),
  };
}

export function isPersistableLegend(item: BoardLegendItem) {
  return item.source === "db" && typeof item.id === "number";
}

export function legendChipClassName(item: BoardLegendItem) {
  if (item.visual_style === "dashed") {
    return `${LEGEND_CHIP_SHAPE} ${IDEA_OUTLINE_CLASS} cursor-pointer`;
  }
  const fill = LEGEND_SOLID_FILL[item.line_key] ?? "bg-[#737373]";
  return `${LEGEND_CHIP_SHAPE} text-white cursor-pointer ${fill}`;
}

export function legendChipStyle(
  item: BoardLegendItem,
): CSSProperties | undefined {
  if (item.visual_style === "dashed") {
    const style: CSSProperties = {};
    if (item.outline_hex) style.borderColor = item.outline_hex;
    if (item.outline_text_hex) style.color = item.outline_text_hex;
    return Object.keys(style).length ? style : undefined;
  }
  const style: CSSProperties = {};
  if (item.bar_hex) style.backgroundColor = item.bar_hex;
  if (item.bar_text_hex) style.color = item.bar_text_hex;
  return Object.keys(style).length ? style : undefined;
}

export function canLoadLegendFromSupabase() {
  if (import.meta.env.MODE === "test") return false;
  return Boolean(
    import.meta.env.VITE_SUPABASE_URL &&
    import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  );
}

export async function loadLegendFromSupabase(): Promise<
  BoardLegendItem[] | null
> {
  if (!canLoadLegendFromSupabase()) return null;
  try {
    const { data, error } = await getSupabaseClient()
      .from("board_legend_items")
      .select("*")
      .eq("active", true)
      .order("sort_order", { ascending: true });
    if (error || !data?.length) return null;
    return data.map((row) => rowToLegendItem(row));
  } catch {
    return null;
  }
}

export async function saveLegendItem(id: number, patch: BoardLegendPatch) {
  const body = {
    ...allowedLegendPatch(patch),
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await getSupabaseClient()
    .from("board_legend_items")
    .update(body)
    .eq("id", id)
    .select("*")
    .single();
  if (error || !data) {
    throw new Error(error?.message ?? "保存图例失败");
  }
  return rowToLegendItem(data);
}
